/**
 * CreativeHostingPage — asset library for creatives attached to a campaign.
 *
 * Layout (top to bottom):
 *   1. Header (gradient headline).
 *   2. Campaign picker + upload input (<= 50 MB) + type filter.
 *   3. Grid of cards: thumbnail + type badge + size, with Delete + Copy-URL
 *      actions per card.
 *
 * The actual blob upload lives elsewhere (R2). This page only registers
 * creative metadata via POST /api/v1/creative-hosting/. Tests mock fetch
 * so neither upload nor R2 is exercised here.
 */
import {
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type FormEvent,
} from "react";
import {
	useCreativeHostingList,
	useCreativeHostingCreate,
	useCreativeHostingDelete,
} from "./hooks";
import {
	CREATIVE_TYPES,
	MAX_UPLOAD_BYTES,
	buildPreviewUrl,
	formatBytes,
	type CreativeHostingRow,
	type CreativeType,
} from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const SELECT =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";

/** Tailwind classes per creative type badge. Kept on-brand (zinc/emerald/sky). */
const TYPE_BADGE: Record<CreativeType, string> = {
	banner: "bg-emerald-50 text-emerald-700 border-emerald-200",
	video: "bg-sky-50 text-sky-700 border-sky-200",
	html: "bg-zinc-100 text-zinc-700 border-zinc-300",
	native: "bg-emerald-50 text-emerald-700 border-emerald-200",
	email: "bg-sky-50 text-sky-700 border-sky-200",
	lp: "bg-emerald-50 text-emerald-700 border-emerald-200",
	drive_link: "bg-zinc-100 text-zinc-700 border-zinc-300",
	other: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

/** Derive a display-type from a File. Keeps the POST body correct without
 *  forcing users to pick manually. */
function typeFromFile(f: File): CreativeType {
	const mime = (f.type || "").toLowerCase();
	const name = f.name.toLowerCase();
	if (mime.startsWith("video/")) return "video";
	if (mime.startsWith("image/")) return "banner";
	if (mime === "text/html" || name.endsWith(".html") || name.endsWith(".htm"))
		return "html";
	if (
		mime === "message/rfc822" ||
		name.endsWith(".eml") ||
		name.endsWith(".mjml")
	)
		return "email";
	return "other";
}

type FilterValue = "all" | CreativeType;

/**
 * Thumbnail tile. Banner -> image-ish tile, video -> play glyph, HTML ->
 * angle-brackets, other -> neutral file. All pure CSS/SVG — no network.
 */
function Thumbnail({ type }: { type: CreativeType }) {
	const base =
		"flex h-28 w-full items-center justify-center rounded-[6px] border";
	if (type === "banner" || type === "native") {
		return (
			<div
				className={`${base} border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50`}
				aria-hidden="true"
				data-testid="thumb-image"
			>
				<svg
					viewBox="0 0 24 24"
					className="h-8 w-8 text-emerald-600"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.75"
				>
					<rect x="3" y="4" width="18" height="16" rx="2" />
					<circle cx="9" cy="10" r="2" />
					<path d="M3 18l6-6 4 4 3-3 5 5" />
				</svg>
			</div>
		);
	}
	if (type === "video") {
		return (
			<div
				className={`${base} border-sky-200 bg-zinc-50`}
				aria-hidden="true"
				data-testid="thumb-video"
			>
				<svg
					viewBox="0 0 24 24"
					className="h-8 w-8 text-sky-600"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.75"
				>
					<polygon points="9 7 17 12 9 17 9 7" fill="currentColor" />
					<rect x="3" y="4" width="18" height="16" rx="2" />
				</svg>
			</div>
		);
	}
	if (type === "html" || type === "lp") {
		return (
			<div
				className={`${base} border-zinc-300 bg-zinc-50`}
				aria-hidden="true"
				data-testid="thumb-html"
			>
				<span className="font-mono text-sm text-zinc-600">
					&lt;/&gt;
				</span>
			</div>
		);
	}
	return (
		<div
			className={`${base} border-zinc-300 bg-zinc-50`}
			aria-hidden="true"
			data-testid="thumb-other"
		>
			<svg
				viewBox="0 0 24 24"
				className="h-8 w-8 text-zinc-500"
				fill="none"
				stroke="currentColor"
				strokeWidth="1.75"
			>
				<path d="M6 2h8l6 6v14H6z" />
				<path d="M14 2v6h6" />
			</svg>
		</div>
	);
}

function AssetCard({
	row,
	onDelete,
	onCopy,
	deleting,
	copied,
}: {
	row: CreativeHostingRow;
	onDelete: () => void;
	onCopy: () => void;
	deleting: boolean;
	copied: boolean;
}) {
	return (
		<article
			data-testid={`creative-hosting-card-${row.id}`}
			className="flex flex-col gap-3 rounded-[8px] border border-zinc-200 bg-white p-4 shadow-sm"
		>
			<Thumbnail type={row.type} />
			<div className="flex items-center justify-between gap-2">
				<span
					className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${TYPE_BADGE[row.type]}`}
					data-testid={`creative-hosting-badge-${row.id}`}
				>
					{row.type}
				</span>
				<span className="text-xs text-zinc-500" data-testid={`creative-hosting-size-${row.id}`}>
					{formatBytes(row.size_bytes)}
				</span>
			</div>
			<div>
				<p
					className="truncate text-sm font-medium text-zinc-900"
					title={row.label}
					data-testid={`creative-hosting-label-${row.id}`}
				>
					{row.label}
				</p>
				<p className="truncate text-xs text-zinc-500" title={row.storage_key}>
					{row.storage_key}
				</p>
			</div>
			<div className="mt-auto flex items-center justify-between gap-2 pt-1">
				<button
					type="button"
					className={BTN_GHOST}
					onClick={onCopy}
					data-testid={`creative-hosting-copy-${row.id}`}
				>
					{copied ? "Copied" : "Copy URL"}
				</button>
				<button
					type="button"
					className={BTN_DANGER}
					onClick={onDelete}
					disabled={deleting}
					data-testid={`creative-hosting-delete-${row.id}`}
				>
					Delete
				</button>
			</div>
		</article>
	);
}

export function CreativeHostingPage() {
	const [campaignId, setCampaignId] = useState("");
	const [typeFilter, setTypeFilter] = useState<FilterValue>("all");
	const [uploadErr, setUploadErr] = useState<string | null>(null);
	const [copiedId, setCopiedId] = useState<string | null>(null);
	const fileRef = useRef<HTMLInputElement | null>(null);

	const listQuery = useCreativeHostingList(campaignId);
	const createMut = useCreativeHostingCreate();
	const deleteMut = useCreativeHostingDelete();

	const items = listQuery.data?.items ?? [];
	const filtered = useMemo(() => {
		if (typeFilter === "all") return items;
		return items.filter((r) => r.type === typeFilter);
	}, [items, typeFilter]);

	const handleCampaignSubmit = (e: FormEvent) => {
		e.preventDefault();
		// useQuery is enabled on non-empty campaignId, so nothing to do here
		// beyond accepting the form submission. Kept for keyboard UX.
	};

	const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
		setUploadErr(null);
		const f = e.target.files?.[0];
		if (!f) return;
		if (!campaignId.trim()) {
			setUploadErr("Enter a campaign id before uploading.");
			e.target.value = "";
			return;
		}
		if (f.size > MAX_UPLOAD_BYTES) {
			setUploadErr(
				`File too large: ${formatBytes(f.size)} exceeds ${formatBytes(MAX_UPLOAD_BYTES)}.`,
			);
			e.target.value = "";
			return;
		}
		const body = {
			campaignId: campaignId.trim(),
			label: f.name.slice(0, 200),
			type: typeFromFile(f),
			// Deterministic placeholder key; the real R2 upload path lives in a
			// separate service. The API accepts any opaque, non-empty string.
			storageKey: `uploads/${campaignId.trim()}/${Date.now()}-${f.name}`,
			sizeBytes: f.size,
			mime: f.type || "application/octet-stream",
		};
		createMut.mutate(body, {
			onSettled: () => {
				if (fileRef.current) fileRef.current.value = "";
			},
			onError: (err) => {
				setUploadErr(err.message);
			},
		});
	};

	const handleCopy = async (row: CreativeHostingRow) => {
		const url = buildPreviewUrl(row);
		try {
			if (
				typeof navigator !== "undefined" &&
				navigator.clipboard &&
				typeof navigator.clipboard.writeText === "function"
			) {
				await navigator.clipboard.writeText(url);
			}
			setCopiedId(row.id);
			setTimeout(() => {
				setCopiedId((current) => (current === row.id ? null : current));
			}, 1500);
		} catch {
			// Clipboard blocked (e.g. iframe/test env). Still flip the label so
			// the user gets feedback; the URL is visible in the thumbnail line.
			setCopiedId(row.id);
		}
	};

	return (
		<main
			data-testid="creative-hosting-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Asset library
				</p>
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Creative hosting
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Upload banners, video, HTML, and landing pages per campaign. Copy
					the preview URL to share with partners.
				</p>
			</header>

			<section className={CARD} aria-label="Campaign + upload controls">
				<form
					onSubmit={handleCampaignSubmit}
					className="flex flex-col gap-4 md:flex-row md:items-end"
					data-testid="creative-hosting-controls"
				>
					<label className="flex flex-1 flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Campaign id
						</span>
						<input
							type="text"
							className={INPUT}
							value={campaignId}
							onChange={(e) => setCampaignId(e.currentTarget.value)}
							placeholder="e.g. spring-launch"
							data-testid="creative-hosting-campaign-input"
						/>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Filter
						</span>
						<select
							className={SELECT}
							value={typeFilter}
							onChange={(e) =>
								setTypeFilter(e.currentTarget.value as FilterValue)
							}
							data-testid="creative-hosting-type-filter"
						>
							<option value="all">All types</option>
							{CREATIVE_TYPES.map((t) => (
								<option key={t} value={t}>
									{t}
								</option>
							))}
						</select>
					</label>
					<label className="flex flex-col gap-1">
						<span className="text-xs font-medium text-zinc-600">
							Upload (max 50 MB)
						</span>
						<input
							ref={fileRef}
							type="file"
							className="block w-full cursor-pointer rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm text-zinc-700 file:mr-3 file:rounded-[6px] file:border-0 file:bg-gradient-to-r file:from-emerald-500 file:to-sky-500 file:px-3 file:py-1 file:text-xs file:font-medium file:text-white hover:file:opacity-90"
							onChange={handleFile}
							disabled={createMut.isPending || !campaignId.trim()}
							data-testid="creative-hosting-upload"
						/>
					</label>
				</form>
				{uploadErr ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="creative-hosting-upload-error"
					>
						{uploadErr}
					</p>
				) : null}
				{createMut.error ? (
					<p
						className="mt-3 text-xs text-red-600"
						role="alert"
						data-testid="creative-hosting-create-error"
					>
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section aria-label="Creative assets">
				{!campaignId.trim() ? (
					<div className={CARD}>
						<p className="text-sm text-zinc-500" data-testid="creative-hosting-hint">
							Enter a campaign id to view its asset library.
						</p>
					</div>
				) : listQuery.isLoading ? (
					<div className={CARD}>
						<p
							className="text-sm text-zinc-500"
							data-testid="creative-hosting-loading"
						>
							Loading.
						</p>
					</div>
				) : listQuery.error ? (
					<div className={CARD}>
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="creative-hosting-list-error"
						>
							{listQuery.error.message}
						</p>
					</div>
				) : filtered.length === 0 ? (
					<div className={CARD}>
						<p
							className="text-sm text-zinc-500"
							data-testid="creative-hosting-empty"
						>
							{items.length === 0
								? "No creatives uploaded yet."
								: "No creatives match this filter."}
						</p>
					</div>
				) : (
					<ul
						className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
						data-testid="creative-hosting-grid"
					>
						{filtered.map((row) => (
							<li key={row.id} className="flex">
								<AssetCard
									row={row}
									onDelete={() => deleteMut.mutate(row.id)}
									onCopy={() => handleCopy(row)}
									deleting={
										deleteMut.isPending && deleteMut.variables === row.id
									}
									copied={copiedId === row.id}
								/>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default CreativeHostingPage;
